<template lang="pug">
.native-app-notifications-rapidfire(:class="{'centered': loading}")
  NativeAppSpinnerWrapper(v-if="loading" :active="loading")
  .selection-bar(v-if="!loading")
    a(@click.stop.prevent="checkAll(true)") Check All
    a(@click.stop.prevent="checkAll(false)") Uncheck All
  .preferences-list.fixed-header.rogue-scroll-y(v-if="!loading")
    native-app-list-item(
      v-for="item in items"
      :key="item.id"
      no-icon
    )
      .details(@click="update(item.id)")
        .title {{ item.title }}
        .subtitle {{ item.description }}
      Checkbox(:modelValue="rapidFireModel[item.id]" :id="`notification-checkbox-'${item.id}`" @update:modelValue="update(item.id)")
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import NativeAppSpinnerWrapper from './components/native-app-spinner-wrapper.vue'
import NativeAppListItem from './components/native-app-list-item.vue'
import debounce from '@/js/helpers/debounce'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useRogue } from '@/js/services/Rogue'

type MessageType = {
  id: number,
  type: string,
  guest: boolean,
  title: string,
  description: string,
}

const customerStore = useCustomerStore()
const rogue = useRogue()

const loading = ref<boolean>(true)
const types = JSON.parse(sessionStorage.getItem('nativeAppNotificationsTypes') ?? '[]') as MessageType[]
const items = ref<MessageType[]>(types.filter((type: MessageType) => type.type === 'rapidfire'))
const rapidFireModel = ref<{ [key: number]: boolean }>(JSON.parse(sessionStorage.getItem('nativeAppNotificationsModel') ?? '{}'))
let save: ((...args: any[]) => void) | null = null

const loggedIn = computed<boolean>(() => customerStore.loggedIn)

function update(id: number) {
  rapidFireModel.value[id] = !rapidFireModel.value[id]
  save!()
}

function checkAll(check: boolean = true) {
  items.value.forEach((item: any) => rapidFireModel.value[item.id] = check)
  save!()
}

watch(loggedIn, (value) => {
  items.value = value ? items.value : items.value.filter((item: any) => item.guest)
})

onMounted(async () => {
  await customerStore.init(rogue)
  if (!loggedIn.value) {
    items.value = items.value?.filter((item: any) => item.guest)
  }

  save = debounce(() => {
    const unsubscribedMessageTypeIds: number[] = []
    types.forEach(item => {
      if (!rapidFireModel.value[item.id]) {
        unsubscribedMessageTypeIds.push(item.id)
      }
    })
    const { deviceUid } = rogue.appContext
    rogue.fetch('/svc_push/preference', 'POST', { uid: deviceUid, unsubscribedMessageTypeIds })
  }, 1000)
  loading.value = false
})
</script>

<style scoped lang="scss">
.native-app-notifications-rapidfire.centered {
  display: flex;
  align-items: center;
  justify-content: center;
}
.native-app-list-item {
  padding: $space-xxs $space-xxs $space-xxs $space-xs;
}
.title {
  font-size: $size-s;
  line-height: $line-height-l;
}
.subtitle {
  font-size: $size-xxs;
  color: $color-gray-dark;
}
.fixed-header:before {
  content: '';
  display: flex;
  height: $space-xl;
}
.selection-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  background: #222222;
  z-index: $z-index-sticky;

  a {
    display: inline-block;
    line-height: $space-xl;
    text-decoration: none;
    padding: 0 $space-xs;

    &, &:active, &:hover {
      color: $color-gray-dark;
    }
  }
}

.preferences-list {
  display: flex;
  flex-direction: column;
  gap: $space-xxxs;
  padding: $space-s;
}
</style>
